import React, { useState } from "react";
import { X, Send, Bot, User } from "lucide-react";
import { api } from "@/lib/api";

export default function AIChat({ onClose }) {
  const [messages, setMessages] = useState([
    { from: "bot", text: "Hi! I'm your DishLCO assistant. Ask me about plans, recharges, or billing!" }
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    const msg = input.trim();
    setInput("");
    setMessages(p => [...p, { from: "user", text: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/ai/chat", { message: msg });
      setMessages(p => [...p, { from: "bot", text: data.reply }]);
    } catch {
      setMessages(p => [...p, { from: "bot", text: "Sorry, I'm having trouble right now. Try again later." }]);
    } finally { setSending(false); }
  };

  const getRecommendations = async () => {
    setSending(true);
    setMessages(p => [...p, { from: "user", text: "Suggest the best plan for me" }]);
    try {
      const { data } = await api.post("/ai/recommend", {});
      if (data.recommendations?.length) {
        const lines = data.recommendations.map((r, i) =>
          `${i + 1}. **${r.product.name}** — ₹${r.product.sell_price} (${r.product.validity_days}d)\n   ${r.reasons.join(", ")}`
        ).join("\n\n");
        setMessages(p => [...p, { from: "bot", text: `Here are my top picks for you:\n\n${lines}\n\n${data.reason}` }]);
      } else {
        setMessages(p => [...p, { from: "bot", text: data.reason || "No recommendations available right now." }]);
      }
    } catch {
      setMessages(p => [...p, { from: "bot", text: "Couldn't fetch recommendations. Try again." }]);
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white md:inset-auto md:bottom-4 md:right-4 md:w-96 md:h-[32rem] md:rounded-2xl md:shadow-2xl md:border md:border-slate-200">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-brand-primary to-brand-accent text-white md:rounded-t-2xl">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5" />
          <span className="font-heading font-bold">AI Assistant</span>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg"><X className="w-5 h-5" /></button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
              m.from === "user"
                ? "bg-brand-primary text-white rounded-br-md"
                : "bg-slate-100 text-slate-700 rounded-bl-md"
            }`}>
              {m.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-2.5 text-sm text-slate-400">Typing…</div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="px-4 pb-2 flex gap-2 overflow-x-auto">
        <button onClick={getRecommendations} disabled={sending}
          className="flex-shrink-0 text-xs bg-brand-accent/10 text-brand-accent font-semibold px-3 py-1.5 rounded-full hover:bg-brand-accent/20 transition-colors">
          🎯 Suggest a plan
        </button>
        <button onClick={() => { setInput("How do I recharge?"); }} disabled={sending}
          className="flex-shrink-0 text-xs bg-slate-100 text-slate-600 font-semibold px-3 py-1.5 rounded-full hover:bg-slate-200 transition-colors">
          ⚡ How to recharge?
        </button>
      </div>

      {/* Input */}
      <form onSubmit={send} className="flex gap-2 p-4 border-t border-slate-100">
        <input value={input} onChange={e => setInput(e.target.value)} placeholder="Ask me anything…"
          className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-brand-primary" />
        <button type="submit" disabled={sending || !input.trim()}
          className="w-10 h-10 flex items-center justify-center bg-brand-primary text-white rounded-xl disabled:opacity-40">
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
